import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import FloatingThemeToggle from "@/components/FloatingThemeToggle";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { PRIMARY_BTN_CLS } from "@/components/premium";

/**
 * Public activation page used by invited bedriftsledere.
 *
 * Flow:
 *   1. Verifies the invitation token via `GET /api/auth/invitation/:token`.
 *   2. User sets a new password (min. 8 tegn).
 *   3. `POST /api/auth/invitation/:token/activate` returns JWT.
 *   4. We persist the session and redirect to `/dashboard`.
 *
 * Security properties:
 *   - The token only lives in the URL and is never displayed back to the user.
 *   - Errors are intentionally vague (the same generic message regardless of
 *     why the token is invalid) — see the backend for the matching policy.
 */
export default function ActivateInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loadingToken, setLoadingToken] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get(`/auth/invitation/${token}`)
      .then((r) => { if (mounted) setInfo(r.data); })
      .catch((err) => {
        if (mounted) setError(err?.response?.data?.detail || "Aktiverings-lenken er ugyldig eller utløpt.");
      })
      .finally(() => { if (mounted) setLoadingToken(false); });
    return () => { mounted = false; };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Passordet må være minst 8 tegn.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passordene er ikke like.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/auth/invitation/${token}/activate`, { new_password: password });
      // Persist session — match AuthContext keys so a hard reload picks it up.
      localStorage.setItem("kp_token", r.data.access_token);
      localStorage.setItem("kp_user", JSON.stringify(r.data.user));
      localStorage.removeItem("kp_admin_biz_id");
      toast.success("Kontoen din er aktivert.");
      // Hard navigation so AuthProvider re-bootstraps from localStorage.
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke aktivere kontoen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-muted/40 px-4 py-12">
      <SEO
        title="Aktiver kontoen din — ClientFlow"
        description="Sett ditt eget passord og kom i gang med ClientFlow."
        path={`/aktiver/${token}`}
        noindex
      />
      <FloatingThemeToggle />
      <div className="w-full max-w-md bg-card border border-border rounded-2xl p-8 shadow-sm anim-in" data-testid="activate-card">
        <Link to="/" className="flex items-center mb-8" aria-label="ClientFlow">
          <Logo className="h-9 w-auto" />
        </Link>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Aktiver kontoen din</h1>

        {loadingToken && (
          <div className="mt-8 text-muted-foreground text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Verifiserer lenken...
          </div>
        )}

        {!loadingToken && error && (
          <div className="mt-6 rounded-xl border border-rose-500/30 bg-rose-500/[0.05] p-4 text-sm" data-testid="activate-error">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-300 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Lenken kan ikke brukes</p>
                <p className="text-muted-foreground mt-1">{error}</p>
                <p className="text-muted-foreground mt-3">
                  Be ClientFlow-administrator om å sende en ny invitasjon.
                </p>
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/logg-inn"
                className="text-sm text-primary underline-offset-4 hover:underline"
                data-testid="activate-back-to-login"
              >
                Til innlogging
              </Link>
            </div>
          </div>
        )}

        {!loadingToken && !error && info && (
          <>
            <p className="text-muted-foreground mt-1 text-sm">
              {info.owner_name ? `Hei ${info.owner_name}! ` : "Hei! "}
              Du aktiverer kontoen for <span className="font-semibold text-foreground">{info.business_name}</span>.
            </p>
            <div className="mt-5 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.05] p-3 text-sm flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-300 shrink-0 mt-0.5" />
              <div>
                <span className="font-medium">Logg-inn:</span> {info.owner_email}
                <div className="text-xs text-muted-foreground mt-0.5">
                  Velg ditt eget passord nedenfor — det er kun du som ser det.
                </div>
              </div>
            </div>

            <form className="space-y-4 mt-5" onSubmit={onSubmit}>
              <div className="space-y-2">
                <Label htmlFor="password">Nytt passord (minst 8 tegn)</Label>
                <PasswordInput
                  id="password" required minLength={8} value={password}
                  onChange={(e) => setPassword(e.target.value)} autoComplete="new-password"
                  data-testid="activate-password" toggleTestId="activate-password-toggle"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Gjenta passord</Label>
                <PasswordInput
                  id="confirm" required minLength={8} value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password"
                  data-testid="activate-confirm" toggleTestId="activate-confirm-toggle"
                />
              </div>
              <Button
                type="submit" className={`${PRIMARY_BTN_CLS} w-full`} disabled={submitting}
                data-testid="activate-submit"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Aktiver og logg meg inn
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
